import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  collection,
  addDoc,
  getDocs,
  writeBatch,
  doc,
  Query,
} from "firebase/firestore";
import { db } from "../firebase";
import { showSuccessToast, showErrorToast } from "../toastmsg";
import { transformKeysToCamelCase } from "../utils/case";

interface UploadExcelProps<T extends Record<string, any>> {
  collectionName: string;
  /**
   * Transform a row from Excel into the Firestore document shape.
   * Return null/undefined to skip the row.
   */
  transformRow: (row: Record<string, any>) => T | null | undefined;
  /**
   * Given the new row and a map of existing docs (Firestore data), return true if the row should be considered a duplicate and skipped.
   * (e.g., compare by name, or any unique field)
   * The map values are Firestore data, not necessarily T.
   */
  isDuplicate: (row: T, existingDocs: Map<string, any>) => boolean;
  /**
   * How to generate the key for deduplication map (e.g., by name, or id, etc.)
   * Accepts Firestore data (Record<string, any>), not necessarily T.
   */
  getDeduplicationKey: (row: Record<string, any>) => string;
  /**
   * Optional: Custom query for fetching existing documents (for role-based filtering)
   */
  existingDocsQuery?: Query;
  acceptedTypes?: string;
  buttonLabel?: string;

  onUploadComplete?: (result: {
    added: number;
    skipped: number;
    errors: { row: any; error: string }[];
  }) => void;
}

function UploadExcel<T extends Record<string, any> = any>({
  collectionName,
  transformRow,
  isDuplicate,
  getDeduplicationKey,
  existingDocsQuery,
  acceptedTypes = ".xlsx,.xls",
  buttonLabel = "Upload Excel",
  onUploadComplete,
}: UploadExcelProps<T>) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{
    added: number;
    skipped: number;
    errors: { row: any; error: string }[];
  } | null>(null);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      // Parse Excel
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array", cellDates: true });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawJson = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
        raw: false,
      });
      // Convert headers (object keys) to camelCase before any custom transform
      // Keep original keys too for backward compatibility
      const camelHeaderJson = rawJson.map((row) => ({
        ...row,
        ...transformKeysToCamelCase(row),
      }));
      // Transform rows
      const transformed = camelHeaderJson
        .map(transformRow)
        .filter((row): row is T => !!row);
      // Fetch existing docs - use custom query if provided, otherwise fetch all
      const snapshot = existingDocsQuery
        ? await getDocs(existingDocsQuery)
        : await getDocs(collection(db, collectionName));
      const existingDocsMap = new Map<string, any>();
      snapshot.forEach((doc) => {
        const data = doc.data() as Record<string, any>;
        const key = getDeduplicationKey(data);
        if (key) existingDocsMap.set(key, { id: doc.id, ...data });
      });
      // Add new docs, skip duplicates
      let added = 0;
      let skipped = 0;
      const errors: { row: any; error: string }[] = [];

      // Prepare batch operations
      const batch = writeBatch(db);
      const recordsToAdd: T[] = [];

      // First pass: check for duplicates and prepare records
      for (const row of transformed) {
        const isDup = isDuplicate(row, existingDocsMap);

        const newRow = {
          ...row,
          status: isDup ? "Duplicate" : row.status || "",
        };

        if (isDup) {
          skipped++; // still count for dashboard tracking
        }

        recordsToAdd.push(newRow);
      }

      // Second pass: add records to batch
      for (const row of recordsToAdd) {
        try {
          const docRef = doc(collection(db, collectionName));
          batch.set(docRef, row);
          added++;
        } catch (err: any) {
          errors.push({ row, error: err?.message || String(err) });
        }
      }

      // Execute batch if there are records to add
      if (recordsToAdd.length > 0) {
        try {
          await batch.commit();
        } catch (err: any) {
          // If batch fails, all records in the batch fail
          errors.push(
            ...recordsToAdd.map((row) => ({
              row,
              error: err?.message || String(err),
            }))
          );
          added = 0; // Reset added count since batch failed
        }
      }
      const result = { added, skipped, errors };
      setResult(result);
      const errorDetails =
        errors.length > 0 ? `\nError example: ${errors[0].error}` : "";

      showSuccessToast(
        `Upload complete: ${added} added, ${skipped} Duplicated, ${errors.length} errors.${errorDetails}`
      );

      // ✅ Call onUploadComplete callback if provided
      if (onUploadComplete) {
        onUploadComplete(result);
      }
    } catch (err: any) {
      showErrorToast("Upload failed: " + (err?.message || String(err)));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <label className="relative cursor-pointer inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md font-medium shadow hover:bg-blue-700 transition-colors duration-150 w-fit">
      <span>{uploading ? "Uploading..." : buttonLabel}</span>
      <input
        type="file"
        accept={acceptedTypes}
        onChange={handleFileUpload}
        disabled={uploading}
        className="absolute inset-0 opacity-0 cursor-pointer"
      />
    </label>
  );
}

export default UploadExcel;
